import React, { useContext, useEffect, useRef, useCallback, useMemo } from "react";
import { LanguageContext } from "../../context/LanguageContext";
import { getText } from "../configure/utils";
import { useAuth } from "../msalAuth/AuthProvider";
import { useEnv } from "./utils";
import collapse from "../../assets/chat/collapse-white-32.png";
import "./selectchat.style.css";

export default function Ariba() {
  const { language } = useContext(LanguageContext);
  const { user } = useAuth();
  const { isValid, values, error } = useEnv(["DIALOGFLOW_CX_PROJECT_ID", "DIALOGFLOW_CX_ARIBA"]);

  const dfMessengerRef = useRef(null);
  const parameterRef = useRef(false);
  const languageRef = useRef(language || "th");
  // const autoDetectLanguageRef = useRef(true);
  const isInitializedRef = useRef(false);
  const NEW_SESSION = useMemo(
    () => [
      {
        type: "button",
        icon: { type: "chevron_right", color: "#0087e2" },
        mode: "blocking",
        text: getText("df_cx.timeout.button"),
        event: { event: "evt-ariba-greeting" },
      },
    ],
    [language]
  );

  const attachEmail = useCallback(() => {
    const email = user?.username;
    if (!email || !dfMessengerRef.current) return;

    dfMessengerRef.current.setQueryParameters({
      parameters: { email: email },
    });
    parameterRef.current = true;
  }, [user?.username]);
  
  const initializeChat = useCallback(
    (messenger) => {
      if (!messenger || isInitializedRef.current) return;

      messenger.startNewSession({ retainHistory: false });
      setTimeout(() => {
        attachEmail();
        messenger.sendRequest("event", "evt-ariba-greeting");
        isInitializedRef.current = true;
      }, 500);
    },
    [attachEmail]
  );
  
  const clearQueryParameters = useCallback(() => {
    if (parameterRef.current && dfMessengerRef.current) {
      dfMessengerRef.current.setQueryParameters({});
      parameterRef.current = false;
    }
  }, []);

  const handleSentRequest = useCallback((event) => {
    const queryInput = event.detail?.data?.requestBody?.queryInput;
    if (queryInput) {
      queryInput.languageCode = languageRef.current;
    }
  }, []);

  const handleChatSession = useCallback(
    (event) => {
      if (!event.detail.message || !dfMessengerRef.current) return;

      isInitializedRef.current = false;
      initializeChat(dfMessengerRef.current);
    },
    [initializeChat]
  );

  const handleSessionExpired = useCallback(() => {
    if (!dfMessengerRef.current) return;

    dfMessengerRef.current.renderCustomText(getText("df_cx.timeout.message"), true);
    dfMessengerRef.current.renderCustomCard(NEW_SESSION);
  }, [NEW_SESSION]);

  const handleClickButton = useCallback(
    (event) => {
      if (!dfMessengerRef.current || !event?.detail) return;
      const messenger = dfMessengerRef.current;

      const { event: evt, text } = event.detail;
      if (evt) {
        messenger.renderCustomText(text, false);
        if (evt === "evt-ariba-greeting") {
          attachEmail();
          return;
        } else {
          clearQueryParameters();
          return;
        }
      }
      clearQueryParameters();
      if (text) {
        messenger.renderCustomText(text, false);
        messenger.sendRequest("query", text);
      } else {
        messenger.sendRequest("event", "flow-failed");
      }
    },
    [clearQueryParameters, attachEmail]
  );

  useEffect(() => {
    languageRef.current = language;
  }, [language]);

  useEffect(() => {
    const messenger = document.querySelector(".ariba df-messenger");
    if (!messenger) return;

    const bubble = messenger.querySelector("df-messenger-chat-bubble");
    if (!bubble) return;
    bubble.openChat();

    dfMessengerRef.current = messenger;
    const timeoutId = initializeChat(messenger);

    return () => {
      clearTimeout(timeoutId);
      isInitializedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const eventHandlers = {
      "df-request-sent": handleSentRequest,
      "df-user-input-entered": clearQueryParameters,
      "df-chip-clicked": clearQueryParameters,
      "df-session-expired": handleSessionExpired,
      "df-button-clicked": handleClickButton,
      "df-ariba-clear-chat-session": handleChatSession,
    };

    Object.entries(eventHandlers).forEach(([event, handler]) => {
      window.addEventListener(event, handler);
    });

    return () => {
      Object.entries(eventHandlers).forEach(([event, handler]) => {
        window.removeEventListener(event, handler);
      });
    };
  }, [handleSentRequest, clearQueryParameters, handleSessionExpired, handleClickButton, handleChatSession]);

  if (!isValid) {
    console.error("Sandee Ariba configuration is invalid:", error);
    return null;
  }

  return (
    <div className="ariba">
      <df-messenger project-id={values.DIALOGFLOW_CX_PROJECT_ID} agent-id={values.DIALOGFLOW_CX_ARIBA} allow-feedback="all" location="asia-southeast1">
        <df-messenger-chat-bubble
          chat-title="SANDEE Chatbot"
          chat-subtitle={getText("sap_ariba.buttonText")}
          allow-fullscreen="always"
          placeholder-text={getText("placeholdertext")}
          chat-collapse-icon={collapse}
        />
      </df-messenger>
    </div>
  );
}
